/// <reference types="@types/googlemaps" />

import { ComponentFactoryResolver, ComponentRef, ViewContainerRef } from '@angular/core';
import { HTTPSearchResultTourMap } from 'src/app/models';
import createGoogleMapMarker from './google-map-marker';
import { MapPopupHotelComponent } from './popup/map-popup-hotel.component';
import {Router} from "@angular/router";
import {AnalyticService} from "../../services/analytic.service";

export class GoogleMapProvider {
  private map: google.maps.Map;
  private bounds: google.maps.LatLngBounds;
  private markers = [];
  private popup: any;
  private canOpenPopup = true;

  private openPopupTimer;
  private closePopupTimer;
  private componentFactoryResolver: ComponentFactoryResolver;
  private componentRef: ComponentRef<MapPopupHotelComponent>;
  private viewContainerRef: ViewContainerRef;

  constructor(
    componentFactoryResolver: ComponentFactoryResolver,
    viewContainerRef: ViewContainerRef,
  ) {
    this.componentFactoryResolver = componentFactoryResolver;
    this.viewContainerRef = viewContainerRef;
  }

  initMap() {
    this.bounds = new google.maps.LatLngBounds();
    this.map = new google.maps.Map(document.getElementById('map'), {
      zoom: 15,
      center: new google.maps.LatLng(0, 0),
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_CENTER,
      },
      streetViewControl: true,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.LEFT_TOP,
      },
      fullscreenControl: true,
      clickableIcons: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.LEFT_BOTTOM,
        mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
      },
    });

    this.map.addListener('mousedown', () => {
      this.canOpenPopup = false;
    });
    this.map.addListener('mouseup', () => {
      this.canOpenPopup = true;
    });

  }

  addMarker(tour: HTTPSearchResultTourMap) {
    const selfProvider = this;
    const Popup = function (position, content) {
      this.position = position;
      content.classList.add('popup-bubble-content');
      const pixelOffset = document.createElement('div');
      pixelOffset.classList.add('popup-bubble-anchor');
      pixelOffset.appendChild(content);

      this.anchor = document.createElement('div');
      this.anchor.classList.add('popup-tip-anchor');
      this.anchor.appendChild(pixelOffset);
      this.stopEventPropagation();
    };
    Popup.prototype = Object.create(google.maps.OverlayView.prototype);
    Popup.prototype.onAdd = function () {
      google.maps.event.addDomListener(this.anchor, 'mouseenter', () => {
        selfProvider.cancelPopupClose();
      });
      google.maps.event.addDomListener(this.anchor, 'mouseleave', (event: MouseEvent) => {
        selfProvider.schedulePopupClose(event.relatedTarget as Node | null);
      });

      this.getPanes().floatPane.appendChild(this.anchor);
    };
    Popup.prototype.onRemove = function () {
      if (this.anchor.parentElement) {
        this.anchor.remove();
      }
    };
    Popup.prototype.draw = function () {
      const divPosition = this.getProjection().fromLatLngToDivPixel(this.position);
      const display = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none';

      if (display === 'block') {
        this.anchor.style.left = divPosition.x + 11 + 'px';
        this.anchor.style.top = divPosition.y + 3 + 'px';
      }
      if (this.anchor.style.display !== display) {
        this.anchor.style.display = display;
      }
    };
    Popup.prototype.stopEventPropagation = function () {
      const anchor = this.anchor;
      anchor.style.cursor = 'auto';
      ['dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart', 'pointerdown'].forEach(function (event) {
        anchor.addEventListener(event, function (e) {
          e.stopPropagation();
        });
      });
    };

    const latLng = new google.maps.LatLng(tour.lat, tour.long);

    const marker = createGoogleMapMarker({
      map: this.map,
      position: latLng,
      title: tour.hotel,
    });

    marker.addListener('mouseover', (event: any) => {
      if (this.openPopupTimer) {
        clearTimeout(this.openPopupTimer);
      }
      this.cancelPopupClose();

      this.openPopupTimer = setTimeout(() => {
        if (this.canOpenPopup) {
          this.removeActiveMarkers();

          const factory = this.componentFactoryResolver.resolveComponentFactory(MapPopupHotelComponent);

          if (this.componentRef) {
            this.componentRef.destroy();
          }

          this.componentRef = factory.create(this.viewContainerRef.parentInjector);
          this.componentRef.instance.tour = tour;
          this.componentRef.instance.detectChanges();
          this.viewContainerRef.insert(this.componentRef.hostView);

          if (this.popup) {
            this.popup.setMap(null);
          }
        }
        this.popup = new Popup(marker.getPosition(), document.getElementById('map-popup'));
        this.popup.setMap(this.map);
      }, 150);
    });

    marker.addListener('mouseout', (event: any) => {
      if (this.openPopupTimer) {
        clearTimeout(this.openPopupTimer);
      }

      this.schedulePopupClose(event.relatedTarget as Node | null);
    });

    this.markers.push(marker);

    this.bounds.extend(latLng);
  }

  private cancelPopupClose(): void {
    clearTimeout(this.closePopupTimer);
    this.closePopupTimer = null;
  }

  private schedulePopupClose(relatedTarget: Node | null = null): void {
    this.cancelPopupClose();
    if (relatedTarget && this.popup?.anchor.contains(relatedTarget)) return;
    this.closePopupTimer = setTimeout(() => {
      this.closePopupTimer = null;
      this.removeActiveMarkers();
      if (this.popup) this.popup.setMap(null);
    }, 800);
  }

  center() {
    this.map.setCenter(this.bounds.getCenter());
    this.map.fitBounds(this.bounds);
  }

  /**
   * Удаление всех маркеров
   */
  removeAllMarker() {
    clearTimeout(this.openPopupTimer);
    this.cancelPopupClose();
    if (this.popup) this.popup.setMap(null);
    const markersCount = this.markers.length;
    for (let i = 0; i < markersCount; i++) {
      this.markers[i].unsubscribeEvents();
      google.maps.event.clearInstanceListeners(this.markers[i]);

      this.markers[i].setMap(null);
    }
    this.bounds = new google.maps.LatLngBounds();
    this.markers = [];
  }

  /**
   * Удаление класса активных маркеров
   */
  private removeActiveMarkers() {
    const elements = Array.from(document.querySelectorAll('.active-google-marker'));
    elements.forEach((element: HTMLElement) => {
      element.classList.remove('active-google-marker');
      element.style.zIndex = '1';
    });
  }
}
